// src/components/script-analysis/HighlightsResult.jsx
import React from "react";
import { Sparkles, Users, MessageSquare, Lightbulb, Heart, TrendingUp } from "lucide-react";

const Section = ({ title, icon: Icon, color, children }) => (
  <div className="rounded-xl p-5 border mb-4" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
    <div className="flex items-center gap-2 mb-3 pb-2 border-b" style={{ borderColor: "var(--color-border-primary)" }}>
      <Icon size={16} style={{ color }} />
      <h3 className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const Card = ({ children }) => (
  <div className="rounded-lg p-3 mb-2" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
    {children}
  </div>
);

const HighlightsResult = ({ data, theme }) => {
  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Summary */}
      {data.summary && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} style={{ color: theme.colors.accentPrimary }} />
            <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>亮点概述</span>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.summary}</p>
        </div>
      )}

      {/* Plot Highlights */}
      {data.plotHighlights?.length > 0 && (
        <Section title="情节亮点" icon={Sparkles} color={theme.colors.accentPrimary}>
          {data.plotHighlights.map((item, i) => (
            <Card key={i}>
              <div className="text-xs font-semibold mb-1" style={{ color: "var(--color-text-primary)" }}>{item.title}</div>
              <p className="text-2xs leading-relaxed mb-1" style={{ color: "var(--color-text-secondary)" }}>{item.description}</p>
              <p className="text-2xs italic" style={{ color: "var(--color-text-muted)" }}>戏剧功能：{item.dramaticFunction}</p>
            </Card>
          ))}
        </Section>
      )}

      {/* Character Highlights */}
      {data.characterHighlights?.length > 0 && (
        <Section title="人物亮点" icon={Users} color={theme.colors.purplePrimary}>
          {data.characterHighlights.map((item, i) => (
            <Card key={i}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xs px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${theme.colors.purplePrimary}20`, color: theme.colors.purplePrimary }}>{item.character}</span>
              </div>
              <p className="text-2xs leading-relaxed mb-1" style={{ color: "var(--color-text-secondary)" }}>{item.highlight}</p>
              <p className="text-2xs italic" style={{ color: "var(--color-text-muted)" }}>吸引力：{item.appeal}</p>
            </Card>
          ))}
        </Section>
      )}

      {/* Memorable Dialogues */}
      {data.memorableDialogues?.length > 0 && (
        <Section title="台词金句" icon={MessageSquare} color={theme.colors.bluePrimary}>
          {data.memorableDialogues.map((item, i) => (
            <Card key={i}>
              <div className="pl-3 mb-2" style={{ borderLeft: `2px solid ${theme.colors.bluePrimary}` }}>
                <p className="text-xs font-medium italic" style={{ color: "var(--color-text-primary)" }}>"{item.quote}"</p>
              </div>
              <div className="flex items-center gap-3 text-2xs" style={{ color: "var(--color-text-muted)" }}>
                <span>— {item.speaker}</span>
                {item.context && <span>| {item.context}</span>}
              </div>
              {item.impact && <p className="text-2xs mt-1" style={{ color: "var(--color-text-muted)" }}>{item.impact}</p>}
            </Card>
          ))}
        </Section>
      )}

      {/* Creative Highlights */}
      {data.creativeHighlights?.length > 0 && (
        <Section title="创意亮点" icon={Lightbulb} color={theme.colors.greenPrimary}>
          {data.creativeHighlights.map((item, i) => (
            <Card key={i}>
              <div className="text-xs font-semibold mb-1" style={{ color: "var(--color-text-primary)" }}>{item.element}</div>
              <p className="text-2xs leading-relaxed mb-1" style={{ color: "var(--color-text-secondary)" }}>{item.description}</p>
              <p className="text-2xs italic" style={{ color: "var(--color-text-muted)" }}>独特性：{item.uniqueness}</p>
            </Card>
          ))}
        </Section>
      )}

      {/* Emotional Moments */}
      {data.emotionalMoments?.length > 0 && (
        <Section title="情感高光" icon={Heart} color={theme.colors.pinkPrimary}>
          {data.emotionalMoments.map((item, i) => (
            <Card key={i}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>{item.moment}</span>
                <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${theme.colors.pinkPrimary}20`, color: theme.colors.pinkPrimary }}>{item.emotion}</span>
              </div>
              <p className="text-2xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{item.resonance}</p>
            </Card>
          ))}
        </Section>
      )}

      {/* Commercial Selling Points */}
      {data.commercialSellingPoints?.length > 0 && (
        <Section title="商业卖点" icon={TrendingUp} color={theme.colors.tealPrimary}>
          {data.commercialSellingPoints.map((item, i) => (
            <Card key={i}>
              <div className="text-xs font-semibold mb-1" style={{ color: "var(--color-text-primary)" }}>{item.point}</div>
              <div className="flex items-center gap-3 text-2xs" style={{ color: "var(--color-text-muted)" }}>
                <span>目标受众：{item.targetAudience}</span>
              </div>
              <p className="text-2xs mt-1 leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{item.marketValue}</p>
            </Card>
          ))}
        </Section>
      )}
    </div>
  );
};

export default HighlightsResult;
